import React from "react";
import { DatePicker } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import ruRU from "antd/locale/ru_RU";
import styled from "styled-components";

// Устанавливаем русскую локализацию для dayjs
dayjs.locale("ru");

const { RangePicker } = DatePicker;

interface AntDateRangePickerProps {
  dateFromValue?: string;
  dateToValue?: string;
  timeFromValue?: string;
  timeToValue?: string;
  onDateFromChange: (date: string) => void;
  onDateToChange: (date: string) => void;
  onTimeFromChange: (time: string) => void;
  onTimeToChange: (time: string) => void;
  onOk?: (
    dateFrom: string,
    dateTo: string,
    timeFrom: string,
    timeTo: string
  ) => void;
  placeholder?: [string, string];
  label?: string;
}

const PickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

const PickerLabel = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
  font-weight: 500;
`;

const StyledRangePicker = styled(RangePicker)`
  width: 100%;
  max-width: 280px;

  /* Кастомизация под стиль приложения */
  &.ant-picker {
    border: 1px solid var(--border-divider);
    border-radius: 0.375rem;
    padding: 0.25rem 0.5rem;
    font-size: 0.8125rem;
    transition: all 0.2s;
    box-shadow: none;
    min-height: 28px;
    height: 32px;

    &:hover {
      border-color: var(--border-input);
    }

    &.ant-picker-focused {
      border-color: var(--accent-primary);
      box-shadow: var(--focus-ring);
    }
  }

  .ant-picker-input > input {
    font-size: 0.8125rem;
    color: var(--text-primary);
    padding: 0;

    &::placeholder {
      color: var(--text-muted);
      font-size: 0.75rem;
    }
  }

  .ant-picker-separator {
    color: var(--text-muted);
  }

  .ant-picker-suffix {
    color: var(--text-muted);
  }

  .ant-picker-clear {
    color: var(--text-muted);
    background: transparent;

    &:hover {
      color: var(--text-primary);
      background: var(--bg-secondary);
    }
  }
`;

// Глобальные стили для dropdown календаря
const GlobalCalendarStyles = styled.div`
  /* Стилизация dropdown календаря */
  .ant-picker-dropdown {
    .ant-picker-panel-container {
      border-radius: 0.5rem;
      box-shadow: var(--shadow-lg);
      border: 1px solid var(--border-divider);
      background: var(--bg-card);
    }

    .ant-picker-header {
      border-bottom: 1px solid var(--border-divider);

      .ant-picker-header-view {
        .ant-picker-month-btn,
        .ant-picker-year-btn {
          color: var(--text-primary);
          font-weight: 600;

          &:hover {
            color: var(--accent-primary);
          }
        }
      }

      .ant-picker-header-super-prev-btn,
      .ant-picker-header-prev-btn,
      .ant-picker-header-next-btn,
      .ant-picker-header-super-next-btn {
        color: var(--text-muted);

        &:hover {
          color: var(--accent-primary);
          background: var(--bg-secondary);
        }
      }
    }

    .ant-picker-content {
      .ant-picker-cell {
        &::before {
          border-radius: 0.375rem;
        }

        .ant-picker-cell-inner {
          border-radius: 0.375rem;
          transition: all 0.2s;
        }

        &:hover:not(.ant-picker-cell-selected):not(
            .ant-picker-cell-range-start
          ):not(.ant-picker-cell-range-end) {
          .ant-picker-cell-inner {
            background: var(--bg-secondary);
          }
        }

        &.ant-picker-cell-today .ant-picker-cell-inner {
          border-color: var(--accent-primary);
          color: var(--accent-primary);
          font-weight: 600;
        }

        &.ant-picker-cell-selected .ant-picker-cell-inner,
        &.ant-picker-cell-range-start .ant-picker-cell-inner,
        &.ant-picker-cell-range-end .ant-picker-cell-inner {
          background: var(--accent-primary);
          color: #ffffff;
          font-weight: 600;
        }

        &.ant-picker-cell-in-range .ant-picker-cell-inner {
          background: var(--alert-info-bg);
          color: var(--accent-primary);
        }

        &.ant-picker-cell-range-hover-start .ant-picker-cell-inner,
        &.ant-picker-cell-range-hover-end .ant-picker-cell-inner,
        &.ant-picker-cell-range-hover .ant-picker-cell-inner {
          background: var(--alert-info-bg);
          color: var(--accent-primary);
        }
      }
    }

    .ant-picker-time-panel {
      border-left: 1px solid var(--border-divider);

      .ant-picker-time-panel-column {
        .ant-picker-time-panel-cell {
          color: var(--text-primary);

          &:hover {
            background: var(--bg-secondary);
          }

          &.ant-picker-time-panel-cell-selected {
            background: var(--accent-primary);
            color: #ffffff;
            font-weight: 500;
          }
        }
      }
    }

    .ant-picker-footer {
      border-top: 1px solid var(--border-divider);
      padding: 0.5rem 0.75rem;

      .ant-picker-now-btn {
        color: var(--accent-primary);

        &:hover {
          color: var(--accent-primary-hover);
        }
      }
    }
  }
`;

export const AntDateRangePicker: React.FC<AntDateRangePickerProps> = ({
  dateFromValue = "",
  dateToValue = "",
  timeFromValue = "",
  timeToValue = "",
  onDateFromChange,
  onDateToChange,
  onTimeFromChange,
  onTimeToChange,
  onOk,
  placeholder = ["Дата и время начала", "Дата и время окончания"],
  label,
}) => {
  // Создаем Dayjs объекты из строковых значений
  const createDayjsValues = (): [Dayjs | null, Dayjs | null] => {
    let startWithTime: Dayjs | null = null;
    let endWithTime: Dayjs | null = null;

    // Создаем startDate с временем
    if (dateFromValue) {
      startWithTime = dayjs(dateFromValue);
      if (timeFromValue) {
        const [hours, minutes] = timeFromValue.split(":");
        startWithTime = startWithTime
          .hour(parseInt(hours, 10) || 0)
          .minute(parseInt(minutes, 10) || 0)
          .second(0);
      } else {
        // Если время не указано, ставим 00:00
        startWithTime = startWithTime.hour(0).minute(0).second(0);
      }
    }

    // Создаем endDate с временем
    if (dateToValue) {
      endWithTime = dayjs(dateToValue);
      if (timeToValue) {
        const [hours, minutes] = timeToValue.split(":");
        endWithTime = endWithTime
          .hour(parseInt(hours, 10) || 0)
          .minute(parseInt(minutes, 10) || 0)
          .second(0);
      } else {
        // Если время не указано, ставим 23:59
        endWithTime = endWithTime.hour(23).minute(59).second(59);
      }
    }

    return [startWithTime, endWithTime];
  };

  // Общая функция для обработки изменений дат
  const updateDateValues = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!dates) {
      // Полная очистка при null
      onDateFromChange("");
      onDateToChange("");
      onTimeFromChange("");
      onTimeToChange("");
      return;
    }

    const [startDate, endDate] = dates;

    // Обрабатываем startDate
    if (startDate) {
      onDateFromChange(startDate.format("YYYY-MM-DD"));
      onTimeFromChange(startDate.format("HH:mm"));
    } else {
      onDateFromChange("");
      onTimeFromChange("");
    }

    // Обрабатываем endDate
    if (endDate) {
      onDateToChange(endDate.format("YYYY-MM-DD"));
      onTimeToChange(endDate.format("HH:mm"));
    } else {
      onDateToChange("");
      onTimeToChange("");
    }
  };

  const handleRangeChange = (
    dates: [Dayjs | null, Dayjs | null] | null,
    dateStrings: [string, string]
  ) => {
    updateDateValues(dates);
  };

  // Обработчик для кнопки OK - важно для showTime
  const handleOk = (dates: [Dayjs | null, Dayjs | null]) => {
    // Всегда обновляем значения при нажатии OK
    updateDateValues(dates);

    // Всегда вызываем callback, но с информацией о том, полный ли диапазон
    if (onOk) {
      const dateFrom = dates && dates[0] ? dates[0].format("YYYY-MM-DD") : "";
      const dateTo = dates && dates[1] ? dates[1].format("YYYY-MM-DD") : "";
      const timeFrom = dates && dates[0] ? dates[0].format("HH:mm") : "";
      const timeTo = dates && dates[1] ? dates[1].format("HH:mm") : "";

      onOk(dateFrom, dateTo, timeFrom, timeTo);
    }
  };

  const values = createDayjsValues();

  return (
    <>
      <GlobalCalendarStyles />
      <PickerContainer>
        {label && <PickerLabel>{label}</PickerLabel>}
        <StyledRangePicker
          value={values}
          onChange={handleRangeChange}
          onOk={handleOk}
          showTime={{
            format: "HH:mm",
            defaultValue: [dayjs("00:00", "HH:mm"), dayjs("23:59", "HH:mm")],
          }}
          format="DD.MM.YYYY HH:mm"
          placeholder={placeholder}
          locale={ruRU.DatePicker}
          allowClear
          size="middle"
          data-picker-container="true"
          onOpenChange={(open) => {
            // Предотвращаем закрытие родительского фильтра при открытии picker'а
            if (open) {
              const event = new CustomEvent("ant-picker-opened");
              document.dispatchEvent(event);
            }
          }}
        />
      </PickerContainer>
    </>
  );
};
